import { ContentDelete, ContentDownload, ContentEdit, MenuClose, PlaceCemetary } from '@fs/zion-icon'
import {
  Button,
  Row,
  Select,
  TextField,
  useOverlay,
  DangerButton,
  Separator,
  RadioGroup,
  Radio,
  CheckboxGroup,
  Checkbox,
} from '@fs/zion-ui'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePageView } from '@fs/zion-analytics'
import { usePageTitle } from '../../app/PageTitleContext'
import useNotifications from '../../../hooks/useNotifications'
import { updateGroupProperties } from '../../../services/groupsService'
import { getBooleanDisplayValue, isNumber } from '../../../util/StringUtil'
import { PermissionsContext } from '../../app/PermissionsContext'
import DmcDataView from '../../common/DmcDataView'
import { TreeLink } from '../../app/Links'
import DeleteGroupDialogOverlay from '../dialogOverlays/DeleteGroupDialogOverlay'
import TombstoneGroupDialogOverlay from '../dialogOverlays/TombstoneGroupDialogOverlay'
import { GroupContext } from '../GroupContextProvider'
import joinComponents from '../../../util/ElementUtil'
import { toLocaleString } from '../../../util/DateUtil'

export default function GroupSummary() {
  usePageView({ page_detail: 'DMConsole:GroupSummary' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.group.summary'))
  const { isAuthorized } = useContext(PermissionsContext)
  const { error, ok } = useNotifications()
  const [editing, setEditing] = useState(false)
  const { currentGroupData, fetchCurrentGroupData } = useContext(GroupContext)
  const [groupProperties, setGroupProperties] = useState()
  const tombstoneOverlay = useOverlay()
  const deleteOverlay = useOverlay()
  const MAX_MEMBER_LIMIT = 1000
  const JOIN_POLICY_TO_LABEL = {
    OPEN: t('open', 'Open'),
    REQUEST_TO_JOIN: t('requestToJoin', 'Request to join'),
    INVITE_ONLY: t('inviteOnly', 'Invite only'),
  }
  const GROUP_TYPE_TO_LABEL = {
    ORDINANCE_SHARING: t('group.type.ORDINANCE_SHARING', 'Ordinance Sharing'),
    PRIVATE_PERSON_SHARING: t('group.type.PRIVATE_PERSON_SHARING', 'Private Person Sharing'),
    RECORDS_REVIEW: t('group.type.RECORDS_REVIEW', 'Records Review'),
    RESEARCHER_TREE: t('group.type.RESEARCHER_TREE', 'Researcher Tree'),
    SOCIETY_OWNED_TREE: t('group.type.SOCIETY_OWNED_TREE', 'Society Owned Tree'),
  }
  const originalGroupProperties = useMemo(() => {
    return {
      name: currentGroupData.name,
      description: currentGroupData.description,
      tombstoned: currentGroupData.tombstoned,
      joinPolicy: currentGroupData.joinPolicy,
      sizeLimit: currentGroupData.sizeLimit.toString(),
      codeOfConduct: currentGroupData.codeOfConduct,
      treeInitialized: currentGroupData.treeInitialized,
      types: currentGroupData.types,
      organizationId: currentGroupData.organizationId,
      private: currentGroupData.private,
    }
  }, [currentGroupData])

  const disableSaveBtn = useMemo(() => {
    if (!groupProperties) return true
    return (
      JSON.stringify({
        ...originalGroupProperties,
        types: [...originalGroupProperties.types].sort((a, b) => a.localeCompare(b)),
      }) ===
      JSON.stringify({
        ...groupProperties,
        types: [...(groupProperties?.types || [])].sort((a, b) => a.localeCompare(b)),
      })
    )
  }, [groupProperties, originalGroupProperties])

  useEffect(() => {
    setGroupProperties(originalGroupProperties)
    setEditing(false)
  }, [originalGroupProperties])

  const onGroupPropertiesChange = (fieldType, newValue) => {
    setGroupProperties({
      ...groupProperties,
      [fieldType]: newValue,
    })
  }

  const onSizeLimitChange = (newValue) => {
    setGroupProperties({
      ...groupProperties,
      sizeLimit: Math.min(Math.max(newValue, 0), MAX_MEMBER_LIMIT),
    })
  }

  const saveBtnOnClick = async () => {
    try {
      const groupUpdateProperties = {
        ...groupProperties,
        sizeLimit: groupProperties.sizeLimit,
      }
      await updateGroupProperties(currentGroupData.id, groupUpdateProperties)
      setEditing(false)
      fetchCurrentGroupData()
      ok(t('success', 'Success'))
    } catch (err) {
      error(
        t('groups.errors.editingGroupProperties', {
          defaultValue: `Error editing group properties: ${err.message || ''}`,
          message: err.message || '',
        }),
        err
      )
    }
  }

  const cancelBtnOnClick = () => {
    setEditing(false)
    setGroupProperties(originalGroupProperties)
  }

  const getGroupTypes = () => {
    let groupTypes = ''
    if (currentGroupData.types && currentGroupData.types.length > 0) {
      currentGroupData.types.forEach((type) => {
        groupTypes += `${t(`group.type.${type}`, type)}, `
      })
      groupTypes = groupTypes.slice(0, -2)
    }
    return groupTypes
  }

  const groupSummaryData = [
    {
      key: 'groupName',
      label: t('name', 'Name'),
      detail: editing ? (
        <TextField
          id="Group Name"
          type="text"
          value={groupProperties.name}
          onChange={(e) => onGroupPropertiesChange('name', e.target.value)}
        />
      ) : (
        currentGroupData.name
      ),
    },
    {
      key: 'groupJoinPolicy',
      label: t('group.summary.label.joinPolicy', 'Join Policy'),
      detail: editing ? (
        <Select
          id="Group Join Policy"
          hidePlaceholder
          value={groupProperties.joinPolicy}
          onChange={(e) => onGroupPropertiesChange('joinPolicy', e.target.value)}
          options={Object.entries(JOIN_POLICY_TO_LABEL).map(([joinPolicy, label]) => {
            return {
              value: joinPolicy,
              label,
            }
          })}
        />
      ) : (
        JOIN_POLICY_TO_LABEL[currentGroupData.joinPolicy]
      ),
    },
    {
      key: 'groupCreated',
      label: t('created', 'Created'),
      detail: toLocaleString(new Date(currentGroupData.createDate), false),
    },
    {
      key: 'groupType',
      label: currentGroupData.types.length > 1 ? t('types', 'Types') : t('type', 'Type'),
      detail:
        editing && currentGroupData.types[0] !== 'SOCIETY_OWNED_TREE' ? (
          <CheckboxGroup orientation="horizontal">
            {Object.entries(GROUP_TYPE_TO_LABEL)
              .filter(([value]) => value !== 'SOCIETY_OWNED_TREE')
              .map(([value, label]) => {
                const valueIndexInArrayOfTypes = groupProperties.types.findIndex((type) => type === value)
                const checked = valueIndexInArrayOfTypes !== -1
                return (
                  <Checkbox
                    id={`Group Type ${label}`}
                    key={value}
                    label={label}
                    name={`checkbox-${value}`}
                    checked={checked}
                    onChange={() => {
                      const types = [...groupProperties.types]
                      if (checked) {
                        types.splice(valueIndexInArrayOfTypes, 1)
                      } else {
                        types.push(value)
                      }
                      onGroupPropertiesChange('types', types)
                    }}
                  />
                )
              })}
          </CheckboxGroup>
        ) : (
          getGroupTypes(currentGroupData.types)
        ),
    },
    {
      key: 'groupMemberLimit',
      label: t('group.summary.label.memberLimit', 'Member Limit'),
      detail: editing ? (
        <TextField
          id="Group Member Limit"
          type="text"
          value={groupProperties.sizeLimit}
          onChange={(e) => onSizeLimitChange(e.target.value)}
          helpMessage={
            groupProperties.sizeLimit === MAX_MEMBER_LIMIT
              ? `${t('group.summary.message.maxMemberLimit', 'Maximum Member Limit')}: ${MAX_MEMBER_LIMIT}`
              : null
          }
          errorMessage={
            isNumber(groupProperties.sizeLimit)
              ? null
              : t('group.summary.message.joinPolicyInputError', 'Number required')
          }
        />
      ) : (
        currentGroupData.sizeLimit
      ),
    },
    {
      key: 'groupUpdated',
      label: t('updated', 'Updated'),
      detail: toLocaleString(new Date(currentGroupData.updateDate), false),
    },
    {
      key: 'groupDescription',
      label: t('description', 'Description'),
      detail: editing ? (
        <TextField
          id="Group Description"
          type="text"
          value={groupProperties.description}
          onChange={(e) => onGroupPropertiesChange('description', e.target.value)}
        />
      ) : (
        currentGroupData.description
      ),
    },
    {
      key: 'groupReservationCards',
      label: t('group.summary.label.reservationCards', 'Reservation Cards'),
      detail: currentGroupData.reservationCardCount,
    },
  ]

  if (currentGroupData.private !== null) {
    groupSummaryData.push({
      key: 'groupPrivate',
      label: t('private', 'Private'),
      detail: editing ? (
        <RadioGroup orientation="horizontal">
          <Radio
            name="private-true"
            checked={groupProperties.private === true}
            label={t('yes', 'Yes')}
            value="Yes"
            onChange={() => onGroupPropertiesChange('private', true)}
          />
          <Radio
            name="private-false"
            checked={groupProperties.private === false}
            label={t('no', 'No')}
            value="No"
            onChange={() => onGroupPropertiesChange('private', false)}
          />
        </RadioGroup>
      ) : (
        getBooleanDisplayValue(currentGroupData.private)
      ),
    })
  }
  if (currentGroupData.treeIds) {
    groupSummaryData.push({
      key: 'groupTreeId',
      label: t('treeId', 'Tree ID'),
      detail: joinComponents(
        currentGroupData.treeIds?.map((treeId) => (
          <React.Fragment key={treeId.value}>
            <TreeLink treeId={treeId.value} />
            {treeId.icon && treeId.icon()}
          </React.Fragment>
        )),
        ', '
      ),
    })
  }

  const actions = (
    <Row>
      {editing && (
        <Button Icon={ContentDownload} disabled={disableSaveBtn} emphasis="high" onClick={saveBtnOnClick}>
          {t('action.save', 'Save')}
        </Button>
      )}
      {editing && (
        <Button Icon={MenuClose} emphasis="medium" onClick={cancelBtnOnClick}>
          {t('action.cancel', 'Cancel')}
        </Button>
      )}
      {!editing && (
        <Button Icon={ContentEdit} emphasis="medium" onClick={() => setEditing(!editing)}>
          {t('action.edit', 'Edit')}
        </Button>
      )}
      {!editing && !currentGroupData.tombstoned && (
        <>
          <Button Icon={PlaceCemetary} emphasis="medium" onClick={tombstoneOverlay.handleClick}>
            {t('action.tombstone', 'Tombstone')}
          </Button>
          <TombstoneGroupDialogOverlay {...tombstoneOverlay} />
        </>
      )}
      {!editing && isAuthorized('ManageConfigurationSettingsPermission') && (
        <>
          <DangerButton Icon={ContentDelete} onClick={deleteOverlay.handleClick}>
            {t('action.delete', 'Delete')}
          </DangerButton>
          <DeleteGroupDialogOverlay {...deleteOverlay} />
        </>
      )}
    </Row>
  )

  return (
    <>
      {actions}
      <Separator />
      <DmcDataView id="Group Summary" data={groupSummaryData} />
    </>
  )
}
